import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faSpinner, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import SuccessModal from '../../components/SuccessModal';
import { useAuth } from '../../contexts/AuthContext';

const CreateSurvey = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user is charge_etude or charge_etude_superieur
  // Roles that can choose between CATI/F2F when creating a survey
  const isChargeEtude = user && (user.role === 'charge_etude' || user.role === 'charge_etude_superieur' || user.role === 'resp_controle' || user.role === 'chef_terrain_superieur');
  const isChefTerrain = user && user.role === 'chef_terrain';
  const [formData, setFormData] = useState({
    limesurvey_id: '',
    title: '',
    poste_qcode: '',
    payment_type: 'per_complete',
    price_per_complete: '',
    price_per_day: '',
    target_days: '',
    target_interviewers: '',
    is_active: true,
    client_id: null,
    general_target: '',
    date_debut: '',
    date_fin: '',
    control_percentage: '',
    objectif_quotidien: '',
    consignes: '',
    marge_imprevus: '',
    is_tracker: false,
    waves: []
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // LimeSurvey search states
  const [limeSurveys, setLimeSurveys] = useState([]);
  const [limeSearch, setLimeSearch] = useState('');
  const [showLimeDropdown, setShowLimeDropdown] = useState(false);
  const [selectedLimeSurvey, setSelectedLimeSurvey] = useState(null);

  // Client states
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [creatingClient, setCreatingClient] = useState(false);

  // Default poste_qcode based on role
  useEffect(() => {
    if (isChefTerrain) {
      setFormData(prev => ({ ...prev, poste_qcode: 'ref' }));
    } else if (!isChargeEtude) {
      setFormData(prev => ({ ...prev, poste_qcode: 'Poste' }));
    }
  }, [isChargeEtude, isChefTerrain]);

  // Fetch clients on mount
  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get('/cati/clients');
      if (response.data.success) {
        setClients(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  // Debounced LimeSurvey search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (limeSearch.trim()) {
        searchLimeSurveys(limeSearch);
      } else {
        setLimeSurveys([]);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [limeSearch]);

  const searchLimeSurveys = async (searchTerm) => {
    try {
      console.log('Searching LimeSurvey surveys:', searchTerm);
      const response = await api.get(`/cati/limesurvey/search?search=${encodeURIComponent(searchTerm)}`);
      console.log('LimeSurvey search response:', response.data);

      if (response.data.success) {
        // Convert object to array if needed
        const surveysData = response.data.data || [];
        const surveysArray = Array.isArray(surveysData) ? surveysData : Object.values(surveysData);
        setLimeSurveys(surveysArray);
        console.log('LimeSurvey surveys set:', surveysArray);
      } else {
        console.error('LimeSurvey search failed:', response.data.message);
        setLimeSurveys([]);
      }
    } catch (error) {
      console.error('Error searching LimeSurvey surveys:', error);
      console.error('Error response:', error.response?.data);
      setLimeSurveys([]);
    }
  };

  const handleLimeSurveySelect = (survey) => {
    setSelectedLimeSurvey(survey);
    setFormData(prev => ({
      ...prev,
      limesurvey_id: survey.limesurvey_id,
      title: survey.title
    }));
    setLimeSearch(survey.limesurvey_id);
    setShowLimeDropdown(false);
  };

  const handleLimeSearchChange = (e) => {
    const value = e.target.value;
    setLimeSearch(value);
    setShowLimeDropdown(true);

    // Clear selection if search is cleared
    if (!value.trim()) {
      setSelectedLimeSurvey(null);
      setFormData(prev => ({
        ...prev,
        limesurvey_id: '',
        title: ''
      }));
    }
  };

  const handleLimeSearchFocus = () => {
    setShowLimeDropdown(true);
  };

  const handleLimeSearchBlur = () => {
    // Delay hiding to allow click on dropdown items
    setTimeout(() => {
      setShowLimeDropdown(false);
    }, 200);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Client handlers
  const filteredClients = clients.filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, client_id: client.id }));
    setClientSearch(client.name);
    setShowClientDropdown(false);
  };

  const handleClearClient = () => {
    setSelectedClient(null);
    setFormData(prev => ({ ...prev, client_id: null }));
    setClientSearch('');
  };

  const handleCreateClient = async (name) => {
    try {
      setCreatingClient(true);
      const response = await api.post('/cati/clients', { name });
      if (response.data.success) {
        const newClient = response.data.data;
        setClients(prev => [...prev, newClient].sort((a, b) => a.name.localeCompare(b.name)));
        handleClientSelect(newClient);
      }
    } catch (error) {
      if (error.response?.data?.errors?.name) {
        setErrors(prev => ({ ...prev, client_id: error.response.data.errors.name[0] }));
      }
    } finally {
      setCreatingClient(false);
    }
  };

  // Tracker (vagues) only applies to F2F surveys, where quotas / frais run in waves.
  // Trackers are available for CATI as well as F2F: a vague is a date window with its
  // own objective, and per-vague completes come from productivity filtered on that
  // window — which works for both productivity tables.
  const isTracker = formData.is_tracker;
  const wavesTargetSum = formData.waves.reduce((sum, w) => sum + (parseInt(w.target) || 0), 0);
  const wavesMargeSum = formData.waves.reduce((sum, w) => sum + (parseFloat(w.marge_imprevus) || 0), 0);

  const handleToggleTracker = (checked) => {
    setFormData(prev => ({
      ...prev,
      is_tracker: checked,
      // Seed the first vague from the single date range / objectif already entered.
      waves: checked && prev.waves.length === 0
        ? [{ label: '', date_debut: prev.date_debut || '', date_fin: prev.date_fin || '', target: prev.general_target || '', marge_imprevus: prev.marge_imprevus || '' }]
        : prev.waves
    }));
  };

  const addWave = () => {
    setFormData(prev => ({
      ...prev,
      waves: [...prev.waves, { label: '', date_debut: '', date_fin: '', target: '', marge_imprevus: '' }]
    }));
  };

  const removeWave = (idx) => {
    setFormData(prev => ({
      ...prev,
      waves: prev.waves.filter((_, i) => i !== idx)
    }));
  };

  const updateWave = (idx, field, value) => {
    setFormData(prev => ({
      ...prev,
      waves: prev.waves.map((w, i) => (i === idx ? { ...w, [field]: value } : w))
    }));
    if (errors.waves) {
      setErrors(prev => ({ ...prev, waves: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.limesurvey_id.trim()) {
      newErrors.limesurvey_id = 'L\'ID LimeSurvey est requis';
    }

    if (!formData.title.trim()) {
      newErrors.title = 'Le titre de l\'enquête est requis';
    }

    if (!formData.poste_qcode) {
      newErrors.poste_qcode = 'Veuillez sélectionner la nature de l\'enquête';
    }

    // Per-day payment is only available for CATI surveys (poste_qcode !== 'ref').
    const isPerDay = formData.poste_qcode !== 'ref' && formData.payment_type === 'per_day';

    if (isPerDay) {
      if (!formData.price_per_day || formData.price_per_day === '' || parseFloat(formData.price_per_day) <= 0) {
        newErrors.price_per_day = 'Le prix par jour est requis et doit être supérieur à 0';
      }
      if (!formData.target_days || parseInt(formData.target_days) <= 0) {
        newErrors.target_days = 'Le nombre de jours est requis et doit être supérieur à 0';
      }
      if (!formData.target_interviewers || parseInt(formData.target_interviewers) <= 0) {
        newErrors.target_interviewers = 'Le nombre d\'enquêteurs est requis et doit être supérieur à 0';
      }
    } else {
      if (!formData.price_per_complete || formData.price_per_complete === '' || parseFloat(formData.price_per_complete) <= 0) {
        newErrors.price_per_complete = 'Le prix par enquête complétée est requis et doit être supérieur à 0';
      }
    }

    if (!formData.client_id) {
      newErrors.client_id = 'Le client est requis';
    }

    if (isTracker) {
      // Validate vagues: at least one, each with dates + target, non-overlapping.
      if (formData.waves.length < 1) {
        newErrors.waves = 'Ajoutez au moins une vague';
      } else {
        const sorted = formData.waves
          .map((w, i) => ({ ...w, i }))
          .filter(w => w.date_debut)
          .sort((a, b) => a.date_debut.localeCompare(b.date_debut));

        for (const w of formData.waves) {
          if (!w.date_debut || !w.date_fin) {
            newErrors.waves = 'Chaque vague doit avoir une date de début et une date de fin';
            break;
          }
          if (w.date_fin < w.date_debut) {
            newErrors.waves = 'La date de fin d\'une vague doit être après sa date de début';
            break;
          }
          if (!w.target || parseInt(w.target) <= 0) {
            newErrors.waves = 'Chaque vague doit avoir un objectif (complétions) supérieur à 0';
            break;
          }
        }

        // Non-overlap check (only if no field error above).
        if (!newErrors.waves) {
          for (let i = 1; i < sorted.length; i++) {
            if (sorted[i].date_debut <= sorted[i - 1].date_fin) {
              newErrors.waves = 'Les fenêtres des vagues ne doivent pas se chevaucher';
              break;
            }
          }
        }
      }
    } else {
      if (!formData.general_target || formData.general_target === '' || parseInt(formData.general_target) <= 0) {
        newErrors.general_target = 'L\'objectif général est requis et doit être supérieur à 0';
      }

      if (!formData.date_debut) {
        newErrors.date_debut = 'La date de début est requise';
      }

      if (!formData.date_fin) {
        newErrors.date_fin = 'La date de fin est requise';
      }

      if (formData.date_debut && formData.date_fin && formData.date_fin < formData.date_debut) {
        newErrors.date_fin = 'La date de fin doit être après la date de début';
      }
    }

    if (formData.poste_qcode === 'ref' && formData.objectif_quotidien !== '') {
      const oq = parseInt(formData.objectif_quotidien);
      if (isNaN(oq) || oq < 1) {
        newErrors.objectif_quotidien = "L'objectif quotidien doit être supérieur à 0";
      }
    }

    if (formData.control_percentage === '' || formData.control_percentage === null || formData.control_percentage === undefined) {
      newErrors.control_percentage = 'Le pourcentage de contrôle est requis';
    } else {
      const cp = parseFloat(formData.control_percentage);
      if (isNaN(cp) || cp < 0 || cp > 100) {
        newErrors.control_percentage = 'Le pourcentage doit être entre 0 et 100';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // F2F surveys always use per_complete; per-day is CATI-only.
      const effectivePaymentType = formData.poste_qcode === 'ref' ? 'per_complete' : formData.payment_type;
      const isPerDay = effectivePaymentType === 'per_day';

      const submitData = {
        limesurvey_id: formData.limesurvey_id,
        title: formData.title,
        poste_qcode: formData.poste_qcode,
        payment_type: effectivePaymentType,
        is_active: formData.is_active,
        client_id: formData.client_id || null,
        is_tracker: isTracker,
        // For trackers the backend derives the window + general_target from the vagues.
        date_debut: isTracker ? null : (formData.date_debut || null),
        date_fin: isTracker ? null : (formData.date_fin || null),
        control_percentage: formData.control_percentage !== '' ? parseFloat(formData.control_percentage) : null,
        marge_imprevus: formData.poste_qcode === 'ref' && formData.marge_imprevus !== '' ? parseFloat(formData.marge_imprevus) : null,
        // Objectif quotidien + consignes are F2F-only fields.
        objectif_quotidien: formData.poste_qcode === 'ref' && formData.objectif_quotidien !== '' ? parseInt(formData.objectif_quotidien) : null,
        consignes: formData.poste_qcode === 'ref' && formData.consignes.trim() !== '' ? formData.consignes.trim() : null,
        // Branch fields by payment type
        price_per_complete: isPerDay ? null : parseFloat(formData.price_per_complete),
        general_target: isTracker ? null : (formData.general_target ? parseInt(formData.general_target) : null),
        price_per_day: isPerDay ? parseFloat(formData.price_per_day) : null,
        target_days: isPerDay ? parseInt(formData.target_days) : null,
        target_interviewers: isPerDay ? parseInt(formData.target_interviewers) : null,
        waves: isTracker
          ? formData.waves.map(w => ({
              label: w.label || null,
              date_debut: w.date_debut,
              date_fin: w.date_fin,
              target: parseInt(w.target),
              marge_imprevus: w.marge_imprevus !== '' && w.marge_imprevus != null ? parseFloat(w.marge_imprevus) : 0
            }))
          : undefined,
      };
      const response = await api.post('/cati/surveys', submitData);

      if (response.data.success) {
        setSuccessMessage('Enquête créée avec succès');
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate('/cati/surveys');
        }, 2000);
      }
    } catch (error) {
      console.error('Error creating survey:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Erreur lors de la création de l\'enquête' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/cati/assignments');
  };

  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden py-8">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Créer une Enquête</h1>
            <p className="text-gray-600">
              {isChargeEtude
                ? 'Ajouter une nouvelle enquête LimeSurvey au système'
                : isChefTerrain
                  ? 'Ajouter une nouvelle enquête Face à Face au système'
                  : 'Ajouter une nouvelle enquête LimeSurvey au système CATI'
              }
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* General Error */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 text-sm">{errors.general}</p>
              </div>
            )}

            {/* LimeSurvey ID */}
            <div className="relative">
              <label htmlFor="lime_search" className="block text-sm font-bold text-gray-700 mb-2">
                ID LimeSurvey *
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="lime_search"
                  value={limeSearch}
                  onChange={handleLimeSearchChange}
                  onFocus={handleLimeSearchFocus}
                  onBlur={handleLimeSearchBlur}
                  placeholder="Rechercher par ID ou titre..."
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                    errors.limesurvey_id ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {showLimeDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {limeSurveys.length > 0 ? (
                      limeSurveys.map(survey => (
                        <div
                          key={survey.limesurvey_id}
                          className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleLimeSurveySelect(survey);
                          }}
                        >
                          <div className="font-medium text-gray-900">
                            ID: {survey.limesurvey_id}
                          </div>
                          <div className="text-sm text-gray-500">
                            {survey.title}
                          </div>
                          <div className="text-xs text-gray-400">
                            Langue: {survey.language} | Statut: {survey.active ? 'Actif' : 'Inactif'}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-3 text-gray-500 text-center">
                        {limeSearch.trim() ? 'Aucune enquête trouvée' : 'Tapez pour rechercher...'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {errors.limesurvey_id && (
                <p className="mt-1 text-sm text-red-600">{errors.limesurvey_id}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Recherchez l'enquête par ID ou titre dans LimeSurvey
              </p>
              {selectedLimeSurvey && (
                <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="text-sm text-green-800">
                    <strong>Sélectionné:</strong> {selectedLimeSurvey.title}
                  </div>
                  <div className="text-xs text-green-600">
                    ID: {selectedLimeSurvey.limesurvey_id} | Langue: {selectedLimeSurvey.language}
                  </div>
                </div>
              )}
            </div>

            {/* Survey Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-bold text-gray-700 mb-2">
                Titre de l'Enquête *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Ex: Enquête Satisfaction Client 2025"
                readOnly={selectedLimeSurvey ? true : false}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                } ${selectedLimeSurvey ? 'bg-gray-50 cursor-not-allowed' : ''}`}
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                {selectedLimeSurvey
                  ? 'Titre automatiquement rempli depuis LimeSurvey'
                  : 'Le nom descriptif de l\'enquête'
                }
              </p>
            </div>

            {/* Nature de l'enquête */}
            {isChargeEtude ? (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Nature de l'Enquête *
                </label>
                <div className="flex gap-4">
                  <label
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.poste_qcode === 'Poste'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="poste_qcode"
                      value="Poste"
                      checked={formData.poste_qcode === 'Poste'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <span className="font-medium">CATI</span>
                  </label>
                  <label
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.poste_qcode === 'ref'
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="poste_qcode"
                      value="ref"
                      checked={formData.poste_qcode === 'ref'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <span className="font-medium">Face à Face</span>
                  </label>
                </div>
                {errors.poste_qcode && (
                  <p className="mt-1 text-sm text-red-600">{errors.poste_qcode}</p>
                )}
              </div>
            ) : isChefTerrain ? (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Nature de l'Enquête
                </label>
                <div className="px-4 py-3 border-2 border-blue-500 bg-blue-50 text-blue-700 rounded-lg text-center font-medium">
                  Face à Face
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Nature de l'Enquête
                </label>
                <div className="px-4 py-3 border-2 border-red-500 bg-red-50 text-red-700 rounded-lg text-center font-medium">
                  CATI
                </div>
              </div>
            )}

            {/* Type de Paiement — CATI surveys only (per-day is not available for F2F) */}
            {formData.poste_qcode && formData.poste_qcode !== 'ref' && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Type de Paiement *
                </label>
                <div className="flex gap-4">
                  <label
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.payment_type === 'per_complete'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_type"
                      value="per_complete"
                      checked={formData.payment_type === 'per_complete'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <span className="font-medium">Au questionnaire</span>
                  </label>
                  <label
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-colors ${
                      formData.payment_type === 'per_day'
                        ? 'border-red-500 bg-red-50 text-red-700'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      type="radio"
                      name="payment_type"
                      value="per_day"
                      checked={formData.payment_type === 'per_day'}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <span className="font-medium">À la journée</span>
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Au questionnaire : payé par enquête complétée. À la journée : payé par jour de présence.
                </p>
              </div>
            )}

            {/* Per-complete inputs */}
            {(formData.poste_qcode === 'ref' || formData.payment_type === 'per_complete') && (
              <div>
                <label htmlFor="price_per_complete" className="block text-sm font-bold text-gray-700 mb-2">
                  Prix par Enquête Complétée (MAD) *
                </label>
                <input
                  type="number"
                  id="price_per_complete"
                  name="price_per_complete"
                  value={formData.price_per_complete}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0.01"
                  placeholder="Ex: 5.00"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                    errors.price_per_complete ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.price_per_complete && (
                  <p className="mt-1 text-sm text-red-600">{errors.price_per_complete}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  {isChargeEtude
                    ? 'Montant payé aux enquêteurs pour chaque enquête complétée (obligatoire)'
                    : isChefTerrain
                      ? 'Montant payé aux enquêteurs pour chaque enquête complétée (obligatoire)'
                      : 'Montant payé aux enquêteurs CATI pour chaque enquête complétée (obligatoire)'
                  }
                </p>
              </div>
            )}

            {/* Per-day inputs (CATI only) */}
            {formData.poste_qcode !== 'ref' && formData.payment_type === 'per_day' && (
              <div className="space-y-4 p-4 border-2 border-red-100 rounded-lg bg-red-50/30">
                <div>
                  <label htmlFor="price_per_day" className="block text-sm font-bold text-gray-700 mb-2">
                    Prix par Jour (MAD) *
                  </label>
                  <input
                    type="number"
                    id="price_per_day"
                    name="price_per_day"
                    value={formData.price_per_day}
                    onChange={handleInputChange}
                    step="0.01"
                    min="0.01"
                    placeholder="Ex: 120.00"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      errors.price_per_day ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.price_per_day && (
                    <p className="mt-1 text-sm text-red-600">{errors.price_per_day}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Montant pour une journée complète. Une demi-journée vaut la moitié.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="target_days" className="block text-sm font-bold text-gray-700 mb-2">
                      Nombre de Jours *
                    </label>
                    <input
                      type="number"
                      id="target_days"
                      name="target_days"
                      value={formData.target_days}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="Ex: 10"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.target_days ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {errors.target_days && (
                      <p className="mt-1 text-sm text-red-600">{errors.target_days}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="target_interviewers" className="block text-sm font-bold text-gray-700 mb-2">
                      Nombre d'Enquêteurs *
                    </label>
                    <input
                      type="number"
                      id="target_interviewers"
                      name="target_interviewers"
                      value={formData.target_interviewers}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="Ex: 5"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.target_interviewers ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {errors.target_interviewers && (
                      <p className="mt-1 text-sm text-red-600">{errors.target_interviewers}</p>
                    )}
                  </div>
                </div>
                {formData.price_per_day && formData.target_days && formData.target_interviewers && (
                  <div className="px-4 py-3 bg-white border border-red-200 rounded-lg">
                    <span className="text-sm text-gray-600">Budget estimé : </span>
                    <span className="text-lg font-bold text-red-700">
                      {(parseFloat(formData.price_per_day) * parseInt(formData.target_days) * parseInt(formData.target_interviewers)).toFixed(2)} MAD
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Marge Imprévus - F2F only, and only for non-tracker (tracker sets it per vague) */}
            {formData.poste_qcode === 'ref' && !isTracker && (
              <div>
                <label htmlFor="marge_imprevus" className="block text-sm font-bold text-gray-700 mb-2">
                  Marge Imprévus (MAD)
                </label>
                <input
                  type="number"
                  id="marge_imprevus"
                  name="marge_imprevus"
                  value={formData.marge_imprevus}
                  onChange={handleInputChange}
                  step="0.01"
                  min="0"
                  placeholder="Ex: 5000"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                    errors.marge_imprevus ? 'border-red-500 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.marge_imprevus && (
                  <p className="mt-1 text-sm text-red-600">{errors.marge_imprevus}</p>
                )}
                <p className="mt-1 text-sm text-gray-500">
                  Budget de marge pour les imprévus (dépassements de circuits + bonus) sur les frais de déplacement
                </p>
              </div>
            )}


            {/* Client */}
            <div className="relative">
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Client *
              </label>
              {selectedClient ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 px-4 py-3 border border-green-300 bg-green-50 rounded-lg text-green-800 font-medium">
                    {selectedClient.name}
                  </div>
                  <button
                    type="button"
                    onClick={handleClearClient}
                    className="px-3 py-3 text-gray-500 hover:text-red-600 transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    onBlur={() => setTimeout(() => setShowClientDropdown(false), 200)}
                    placeholder="Rechercher ou créer un client..."
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      errors.client_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {showClientDropdown && (
                    <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.length > 0 ? (
                        filteredClients.map(client => (
                          <div
                            key={client.id}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              handleClientSelect(client);
                            }}
                          >
                            <div className="text-sm font-medium text-gray-900">{client.name}</div>
                          </div>
                        ))
                      ) : !clientSearch.trim() ? (
                        <div className="px-4 py-3 text-gray-500 text-center text-sm">
                          Tapez pour rechercher...
                        </div>
                      ) : null}

                      {clientSearch.trim() && !clients.find(c => c.name.toLowerCase() === clientSearch.trim().toLowerCase()) && (
                        <div
                          className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-t border-gray-200 text-blue-600"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleCreateClient(clientSearch.trim());
                          }}
                        >
                          {creatingClient ? (
                            <span className="flex items-center">
                              <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                              Création...
                            </span>
                          ) : (
                            <span className="flex items-center">
                              <FontAwesomeIcon icon={faPlus} className="mr-2" />
                              Créer le client "{clientSearch.trim()}"
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {errors.client_id && (
                <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Sélectionnez un client existant ou créez-en un nouveau
              </p>
            </div>

            {/* Tracker (vagues) switch — CATI and F2F alike */}
            {(
              <div className="flex items-center justify-between p-4 border-2 border-blue-100 rounded-lg bg-blue-50/30">
                <div className="pr-4">
                  <span className="block text-sm font-bold text-gray-700">Enquête tracker (vagues)</span>
                  <span className="block text-sm text-gray-500">
                    Plusieurs vagues successives, chacune avec sa période et son objectif. L'objectif général est calculé automatiquement.
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer shrink-0">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={formData.is_tracker}
                    onChange={(e) => handleToggleTracker(e.target.checked)}
                  />
                  <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                </label>
              </div>
            )}

            {!isTracker ? (
              <>
                {/* General Target */}
                <div>
                    <label htmlFor="general_target" className="block text-sm font-bold text-gray-700 mb-2">
                      Objectif Général (complétions) *
                    </label>
                    <input
                      type="number"
                      id="general_target"
                      name="general_target"
                      value={formData.general_target}
                      onChange={handleInputChange}
                      min="1"
                      placeholder="Ex: 500"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.general_target ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.general_target && (
                      <p className="mt-1 text-sm text-red-600">{errors.general_target}</p>
                    )}
                    <p className="mt-1 text-sm text-gray-500">
                      Nombre total de complétions attendues pour cette enquête
                    </p>
                </div>

                {/* Date Debut and Date Fin */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="date_debut" className="block text-sm font-bold text-gray-700 mb-2">
                      Date de Début *
                    </label>
                    <input
                      type="date"
                      id="date_debut"
                      name="date_debut"
                      value={formData.date_debut}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.date_debut ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.date_debut && (
                      <p className="mt-1 text-sm text-red-600">{errors.date_debut}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="date_fin" className="block text-sm font-bold text-gray-700 mb-2">
                      Date de Fin *
                    </label>
                    <input
                      type="date"
                      id="date_fin"
                      name="date_fin"
                      value={formData.date_fin}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                        errors.date_fin ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                    {errors.date_fin && (
                      <p className="mt-1 text-sm text-red-600">{errors.date_fin}</p>
                    )}
                  </div>
                </div>
              </>
            ) : (
              /* Vagues editor (tracker) */
              <div className="space-y-4 p-4 border-2 border-blue-100 rounded-lg bg-blue-50/20">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold text-gray-700">Vagues *</span>
                  <button
                    type="button"
                    onClick={addWave}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPlus} /> Ajouter une vague
                  </button>
                </div>

                {errors.waves && (
                  <p className="text-sm text-red-600">{errors.waves}</p>
                )}

                {formData.waves.map((w, idx) => (
                  <div key={idx} className="p-3 bg-white border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-gray-700">Vague {idx + 1}</span>
                      {formData.waves.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeWave(idx)}
                          className="text-gray-400 hover:text-red-600 transition-colors"
                          title="Supprimer la vague"
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      )}
                    </div>
                    <div className={`grid grid-cols-1 sm:grid-cols-2 ${formData.poste_qcode === 'ref' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-3`}>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Date de début</label>
                        <input
                          type="date"
                          value={w.date_debut}
                          onChange={(e) => updateWave(idx, 'date_debut', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Date de fin</label>
                        <input
                          type="date"
                          value={w.date_fin}
                          onChange={(e) => updateWave(idx, 'date_fin', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Objectif (complétions)</label>
                        <input
                          type="number"
                          min="1"
                          value={w.target}
                          onChange={(e) => updateWave(idx, 'target', e.target.value)}
                          placeholder="Ex: 250"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      {/* Marge imprévus — F2F only (travel-cost concept). */}
                      {formData.poste_qcode === 'ref' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Marge imprévus (MAD)</label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={w.marge_imprevus}
                          onChange={(e) => updateWave(idx, 'marge_imprevus', e.target.value)}
                          placeholder="Ex: 5000"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                      </div>
                      )}
                    </div>
                  </div>
                ))}

                <div className="flex items-center justify-between px-4 py-3 bg-white border border-blue-200 rounded-lg">
                  <span className="text-sm font-bold text-gray-700">Objectif Général (complétions)</span>
                  <span className="text-lg font-bold text-blue-700">{wavesTargetSum.toLocaleString()}</span>
                </div>
                <p className="text-xs text-gray-500">Calculé automatiquement à partir des objectifs des vagues.</p>

                {formData.poste_qcode === 'ref' && (
                  <>
                <div className="flex items-center justify-between px-4 py-3 bg-white border border-blue-200 rounded-lg">
                  <span className="text-sm font-bold text-gray-700">Marge Imprévus (total)</span>
                  <span className="text-lg font-bold text-blue-700">{wavesMargeSum.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD</span>
                </div>
                <p className="text-xs text-gray-500">Somme des marges imprévus des vagues.</p>
                  </>
                )}
              </div>
            )}

            {/* Objectif Quotidien + Consignes — F2F only */}
            {formData.poste_qcode === 'ref' && (
              <>
                <div>
                  <label htmlFor="objectif_quotidien" className="block text-sm font-bold text-gray-700 mb-2">
                    Objectif Quotidien (complétions / jour)
                  </label>
                  <input
                    type="number"
                    id="objectif_quotidien"
                    name="objectif_quotidien"
                    value={formData.objectif_quotidien}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="Ex: 8"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                      errors.objectif_quotidien ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.objectif_quotidien && (
                    <p className="mt-1 text-sm text-red-600">{errors.objectif_quotidien}</p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Nombre de complétions attendu par enquêteur et par jour de terrain
                  </p>
                </div>

                <div>
                  <label htmlFor="consignes" className="block text-sm font-bold text-gray-700 mb-2">
                    Consignes
                  </label>
                  <textarea
                    id="consignes"
                    name="consignes"
                    value={formData.consignes}
                    onChange={handleInputChange}
                    rows={6}
                    placeholder="Consignes de terrain pour cette enquête..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Instructions destinées aux équipes terrain (facultatif)
                  </p>
                </div>
              </>
            )}

            {/* Control Percentage */}
            <div>
              <label htmlFor="control_percentage" className="block text-sm font-bold text-gray-700 mb-2">
                Pourcentage de Contrôle Requis (%) *
              </label>
              <input
                type="number"
                id="control_percentage"
                name="control_percentage"
                value={formData.control_percentage}
                onChange={handleInputChange}
                step="0.01"
                min="0"
                max="100"
                placeholder="Ex: 15"
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 ${
                  errors.control_percentage ? 'border-red-500 bg-red-50' : 'border-gray-300'
                }`}
              />
              {errors.control_percentage && (
                <p className="mt-1 text-sm text-red-600">{errors.control_percentage}</p>
              )}
              <p className="mt-1 text-sm text-gray-500">
                Pourcentage des soumissions qui doivent être contrôlées (entre 0 et 100)
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:justify-end gap-3 sm:gap-4 pt-6">
              <button
                type="button"
                onClick={handleCancel}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="w-full sm:w-auto px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {loading ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                    Création...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="mr-2" />
                    Créer l'Enquête
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </div>
  );
};

export default CreateSurvey;
